// @ts-strict-ignore
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { FeatureItem, FixRequest, FixRequestStatusType } from '../../types';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import routes from '../../routes';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    EyeInvisibleOutlined,
    InfoCircleOutlined,
    ToolOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InboxOutlined,
    EditOutlined,
    ClockCircleOutlined,
    EllipsisOutlined,
    LoadingOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { globalMessage } from '../../utils/globalMessage';
import UpdateFixDetailsModal from './UpdateFixDetailsModal';
import Dropdown from 'antd/lib/dropdown';
import { MenuProps } from 'antd/lib/menu';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { IgnoreUnignoreFRItemRequestItem, tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const fixRequestToastKeysByAction = {
    approveSuccess: new Set<string>(),
    approveError: new Set<string>(),
    rejectSuccess: new Set<string>(),
    rejectError: new Set<string>(),
    pendingSuccess: new Set<string>(),
    pendingError: new Set<string>(),
};

export const getFeatureItemActions = (
    requiresFixRequestsApprovals: boolean,
    isItemIgnored: boolean,
    canAddFixRequestForFeatureItem: boolean,
    isReleaseKickedOff: boolean,
    isReleaseSubmitted: boolean,
    shouldPreventFixRequests: boolean,
    fixRequest?: FixRequest,
) => {
    if (!fixRequest) {
        const items: MenuProps['items'] = [
            {
                label: requiresFixRequestsApprovals ? 'Request approval for a fix' : 'Pull in as fix...',
                key: 'addFix',
                icon: <ToolOutlined style={{ color: antGrey.grey7 }} />,
                disabled:
                    !canAddFixRequestForFeatureItem ||
                    !isReleaseKickedOff ||
                    (isReleaseSubmitted && shouldPreventFixRequests),
            },
            {
                label: isItemIgnored ? 'Include' : 'Ignore',
                key: 'includeIgnore',
                icon:
                    isItemIgnored ?
                        <EyeOutlined style={{ color: antGrey.grey7 }} />
                    :   <EyeInvisibleOutlined style={{ color: antGrey.grey7 }} />,
            },
        ];

        return items;
    } else {
        // Fixes that originated from a triage issue carry a triageIssueId; offer a deep-link back to that issue.
        const jumpToTriageItem: MenuProps['items'] =
            fixRequest.triageIssueId ?
                [
                    {
                        label: 'Jump to issue in Triage',
                        key: 'jumpToTriage',
                        icon: <InboxOutlined style={{ color: runwayColors.primary }} />,
                    },
                ]
            :   [];
        if (requiresFixRequestsApprovals) {
            const items: MenuProps['items'] = [
                ...(fixRequest.status?.status !== FixRequestStatusType.FixRequestApproved ?
                    [
                        {
                            label: 'Approve fix',
                            key: 'approveFix',
                            icon: <CheckCircleOutlined style={{ color: runwayColors.statusGreen }} />,
                        },
                    ]
                :   []),
                ...(fixRequest.status?.status !== FixRequestStatusType.FixRequestRejected ?
                    [
                        {
                            label: 'Reject fix',
                            key: 'rejectFix',
                            icon: <CloseCircleOutlined style={{ color: runwayColors.statusRed }} />,
                        },
                    ]
                :   []),
                ...((
                    fixRequest.status?.status === FixRequestStatusType.FixRequestApproved ||
                    fixRequest.status?.status === FixRequestStatusType.FixRequestRejected
                ) ?
                    [
                        {
                            label:
                                fixRequest.status.status === FixRequestStatusType.FixRequestApproved ?
                                    'Rescind approval'
                                :   'Rescind rejection',
                            key: 'moveFixToPending',
                            icon: <ClockCircleOutlined style={{ color: runwayColors.statusYellow }} />,
                        },
                    ]
                :   []),
                {
                    label: 'Edit fix details',
                    key: 'editFixDetails',
                    icon: <EditOutlined style={{ color: antGrey.grey7 }} />,
                },
                ...jumpToTriageItem,
                {
                    label: isItemIgnored ? 'Include' : 'Ignore',
                    key: 'includeIgnore',
                    icon:
                        isItemIgnored ?
                            <EyeOutlined style={{ color: antGrey.grey7 }} />
                        :   <EyeInvisibleOutlined style={{ color: antGrey.grey7 }} />,
                },
            ];

            return items;
        } else {
            const items: MenuProps['items'] = [
                {
                    label: 'Edit fix details',
                    key: 'editFixDetails',
                    icon: <EditOutlined style={{ color: antGrey.grey7 }} />,
                },
                ...jumpToTriageItem,
                {
                    label: isItemIgnored ? 'Include' : 'Ignore',
                    key: 'includeIgnore',
                    icon: <InfoCircleOutlined style={{ color: runwayColors.info }} />,
                },
            ];
            return items;
        }
    }
};

interface FeatureItemActionsProps extends FeatureItem, ReleaseComponentProps {
    workingBranchEnabled: boolean;
    requiresFixRequestsApprovals: boolean;
    canAddFixRequestForFeatureItem: boolean;
    onClickAddFixRequest?: (workItemId: string) => void;
    isReleaseKickedOff: boolean;
    isReleaseSubmitted: boolean;
    shouldPreventFixRequests: boolean;
    stepRunId: string;
}

const FeatureItemActions: React.FC<FeatureItemActionsProps> = (props: FeatureItemActionsProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const featureItem: FeatureItem = {
        id: props.id,
        ticket: props.ticket,
        code: props.code,
        explanationText: props.explanationText,
        ignored: props.ignored,
        autoIgnored: props.autoIgnored,
        isItemDone: props.isItemDone,
        isInLatestOrSelectedStagingBuild: props.isInLatestOrSelectedStagingBuild,
        isInLatestWorkingBranchBuild: props.isInLatestWorkingBranchBuild,
        debugVariant: props.debugVariant,
        associatedStabilityIssues: props.associatedStabilityIssues,
        fixRequest: props.fixRequest,
    };

    const ignoreIsLoading = useIsLoading(ReleaseStepUpdatable.IgnoreFRItem, props.id);
    const ignoreError = useError(ReleaseStepUpdatable.IgnoreFRItem, props.id);

    const unignoreIsLoading = useIsLoading(ReleaseStepUpdatable.UnignoreFRItem, props.id);
    const unignoreError = useError(ReleaseStepUpdatable.UnignoreFRItem, props.id);

    const approveFixRequestIsLoading = useIsLoading(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);
    const approveFixRequestSuccess = useUpdated(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);
    const approveFixRequestError = useError(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);

    const rejectFixRequestIsLoading = useIsLoading(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);
    const rejectFixRequestSuccess = useUpdated(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);
    const rejectFixRequestError = useError(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);

    const moveFixRequestToPendingApprovalIsLoading = useIsLoading(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const moveFixRequestToPendingApprovalSuccess = useUpdated(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const moveFixRequestToPendingApprovalError = useError(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const fixRequestId = props.fixRequest?.id;

    const [isFeatureItemActionLoading, setIsFeatureItemActionLoading] = React.useState(false);
    const [showEditFixDetailsModal, setShowEditFixDetailsModal] = React.useState(false);

    const isIgnored = props.ignored;

    const tryIgnoreUnignoreItem = (ignore: boolean) => {
        const type = ignore ? ReleaseStepUpdatable.IgnoreFRItem : ReleaseStepUpdatable.UnignoreFRItem;

        const items: IgnoreUnignoreFRItemRequestItem[] =
            props.groupedCodeItems ?
                props.groupedCodeItems.map((item) => ({
                    frItemId: item.id,
                    isIgnore: ignore,
                    isAutoIgnoreItem: item.autoIgnored,
                }))
            :   [{ frItemId: props.id, isIgnore: ignore, isAutoIgnoreItem: props.autoIgnored }];

        dispatch(
            tryUpdateReleaseStep(
                type,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    items: items,
                },
                props.id,
            ),
        );
    };

    const tryApproveFixRequest = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.ApproveFixRequest,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    const tryRejectFixRequest = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.RejectFixRequest,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    const tryMoveFixRequestToPendingApproval = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    React.useEffect(() => {
        if (
            ignoreIsLoading ||
            unignoreIsLoading ||
            approveFixRequestIsLoading ||
            rejectFixRequestIsLoading ||
            moveFixRequestToPendingApprovalIsLoading
        ) {
            setIsFeatureItemActionLoading(true);
        } else {
            setIsFeatureItemActionLoading(false);
        }
    }, [
        ignoreIsLoading,
        unignoreIsLoading,
        approveFixRequestIsLoading,
        rejectFixRequestIsLoading,
        moveFixRequestToPendingApprovalIsLoading,
        isFeatureItemActionLoading,
    ]);

    React.useEffect(() => {
        if (unignoreError) {
            globalMessage.error('Item include failed to update. Please try again');
        }
    }, [unignoreError]);

    React.useEffect(() => {
        if (ignoreError) {
            globalMessage.error('Item ignore failed to update. Please try again');
        }
    }, [ignoreError]);

    React.useEffect(() => {
        if (approveFixRequestIsLoading && fixRequestId) {
            fixRequestToastKeysByAction.approveSuccess.delete(fixRequestId);
            fixRequestToastKeysByAction.approveError.delete(fixRequestId);
        }
    }, [approveFixRequestIsLoading, fixRequestId]);

    React.useEffect(() => {
        if (approveFixRequestSuccess && fixRequestId && !fixRequestToastKeysByAction.approveSuccess.has(fixRequestId)) {
            fixRequestToastKeysByAction.approveSuccess.add(fixRequestId);
            globalMessage.success('Fix Request approved successfully!');
        }
    }, [approveFixRequestSuccess, fixRequestId]);

    React.useEffect(() => {
        if (approveFixRequestError && fixRequestId && !fixRequestToastKeysByAction.approveError.has(fixRequestId)) {
            fixRequestToastKeysByAction.approveError.add(fixRequestId);
            globalMessage.error(approveFixRequestError);
        }
    }, [approveFixRequestError, fixRequestId]);

    React.useEffect(() => {
        if (rejectFixRequestIsLoading && fixRequestId) {
            fixRequestToastKeysByAction.rejectSuccess.delete(fixRequestId);
            fixRequestToastKeysByAction.rejectError.delete(fixRequestId);
        }
    }, [rejectFixRequestIsLoading, fixRequestId]);

    React.useEffect(() => {
        if (rejectFixRequestSuccess && fixRequestId && !fixRequestToastKeysByAction.rejectSuccess.has(fixRequestId)) {
            fixRequestToastKeysByAction.rejectSuccess.add(fixRequestId);
            globalMessage.success('Fix Request rejected successfully!');
        }
    }, [rejectFixRequestSuccess, fixRequestId]);

    React.useEffect(() => {
        if (rejectFixRequestError && fixRequestId && !fixRequestToastKeysByAction.rejectError.has(fixRequestId)) {
            fixRequestToastKeysByAction.rejectError.add(fixRequestId);
            globalMessage.error(rejectFixRequestError);
        }
    }, [rejectFixRequestError, fixRequestId]);

    React.useEffect(() => {
        if (moveFixRequestToPendingApprovalIsLoading && fixRequestId) {
            fixRequestToastKeysByAction.pendingSuccess.delete(fixRequestId);
            fixRequestToastKeysByAction.pendingError.delete(fixRequestId);
        }
    }, [moveFixRequestToPendingApprovalIsLoading, fixRequestId]);

    React.useEffect(() => {
        if (
            moveFixRequestToPendingApprovalSuccess &&
            fixRequestId &&
            !fixRequestToastKeysByAction.pendingSuccess.has(fixRequestId)
        ) {
            fixRequestToastKeysByAction.pendingSuccess.add(fixRequestId);
            globalMessage.success('Fix Request moved to pending approval successfully!');
        }
    }, [moveFixRequestToPendingApprovalSuccess, fixRequestId]);

    React.useEffect(() => {
        if (
            moveFixRequestToPendingApprovalError &&
            fixRequestId &&
            !fixRequestToastKeysByAction.pendingError.has(fixRequestId)
        ) {
            fixRequestToastKeysByAction.pendingError.add(fixRequestId);
            globalMessage.error(moveFixRequestToPendingApprovalError);
        }
    }, [moveFixRequestToPendingApprovalError, fixRequestId]);

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        switch (e.key) {
            case 'addFix': {
                props.onClickAddFixRequest(props.id);
                break;
            }
            case 'includeIgnore': {
                tryIgnoreUnignoreItem(!isIgnored);
                break;
            }
            case 'approveFix': {
                tryApproveFixRequest();
                break;
            }
            case 'rejectFix': {
                tryRejectFixRequest();
                break;
            }
            case 'moveFixToPending': {
                tryMoveFixRequestToPendingApproval();
                break;
            }
            case 'editFixDetails': {
                setShowEditFixDetailsModal(true);
                break;
            }
            case 'jumpToTriage': {
                if (props.fixRequest?.triageIssueId) {
                    history.push(
                        generatePath(routes.appTriageIssue, {
                            orgIdParam: props.orgId,
                            appIdParam: props.appId,
                            issueIdParam: props.fixRequest.triageIssueId,
                        }),
                    );
                }
                break;
            }
            default:
                break;
        }
    };

    const items = getFeatureItemActions(
        props.requiresFixRequestsApprovals,
        isIgnored,
        props.canAddFixRequestForFeatureItem,
        props.isReleaseKickedOff,
        props.isReleaseSubmitted,
        props.shouldPreventFixRequests,
        props.fixRequest,
    );

    const menuProps = {
        items,
        onClick: handleMenuClick,
    };

    const isLoading =
        isFeatureItemActionLoading ||
        ignoreIsLoading ||
        approveFixRequestIsLoading ||
        rejectFixRequestIsLoading ||
        moveFixRequestToPendingApprovalIsLoading;

    return (
        <div>
            {showEditFixDetailsModal && props.fixRequest && (
                <UpdateFixDetailsModal
                    orgId={props.orgId}
                    appId={props.appId}
                    stepRunId={props.stepRunId}
                    releaseId={props.releaseId}
                    onCancel={() => setShowEditFixDetailsModal(false)}
                    fixRequest={props.fixRequest}
                    featureItemId={featureItem.id}
                />
            )}
            <Dropdown.Button
                menu={menuProps}
                type="link"
                buttonsRender={([_, rightButton]) => [null, rightButton]}
                icon={
                    isLoading ?
                        <LoadingOutlined style={{ color: antGrey.grey7 }} />
                    :   <EllipsisOutlined style={{ fontSize: '28px', color: antGrey.grey7 }} />
                }
                trigger={['click']}
            ></Dropdown.Button>
        </div>
    );
};

export default FeatureItemActions;
